import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";
import logo from "../../images/logo.png";

const DriverEmployeeAgreement = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const loggedInId = localStorage.getItem("userId");
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [username, setUsername] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [file, setFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");

  /* ---------------- Fetch User Name ---------------- */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${userId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUsername(`${res.data.first_name} ${res.data.last_name}`);
      } catch (err) {
        console.error("Failed to fetch user", err);
      }
    };

    fetchUser();
  }, [API_BASE, isAdmin, userId]);

  /* ---------------- Load Existing Agreement ---------------- */
  useEffect(() => {
    const fetchAgreement = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/driver-agreement/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        if (!res.data) return;

        if (res.data.dea_effective_date) {
          const formattedDate = new Date(res.data.dea_effective_date)
            .toISOString()
            .split("T")[0];

          setEffectiveDate(formattedDate);
        }

        if (res.data.dea_attachment_file) {
          setUploadedFileName(res.data.dea_attachment_file);
          setOriginalFileName(res.data.dea_attachment_original);
        }
      } catch (err) {
        console.error("Failed to load driver agreement", err);
      }
    };

    if (userId) fetchAgreement();
  }, [API_BASE, userId]);

  /* ---------------- Check Task Status ---------------- */
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const TASK_ID = 63; // <-- set your real task id
        const idKey = String(TASK_ID);
        const nameKey = "driver_employee_agreement";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        const signed = status === "review" || status === "completed";
        setIsSigned(signed);

        if (signed) {
          setSignedDate(
            res.data.signdates?.[idKey] ?? res.data.signdates?.[nameKey],
          );
        }
      } catch (err) {
        console.error("Failed to fetch task status", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [API_BASE, userId]);

  /* ---------------- File Upload ---------------- */
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(selected.type)) {
      alert("Only PDF, JPG, PNG files allowed");
      return;
    }

    setFile(selected);
  };

  /* ---------------- Upload Button Handler (FILE ONLY) ---------------- */
  const handleUploadClick = async () => {
    if (!file) {
      alert("Please select a file first");
      return;
    }

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("file", file);

    try {
      const res = await axios.post(
        `${API_BASE}/api/driver-agreement/save`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      alert("Attachment uploaded successfully");

      setUploadedFileName(res.data?.filename ?? null);
      setOriginalFileName(res.data?.originalName ?? null);
      setFile(null);
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed");
    }
  };

  /* ---------------- Effective Date Auto Save ---------------- */
  const handleEffectiveDateChange = async (value) => {
    setEffectiveDate(value);

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("effectiveDate", value);

    try {
      await axios.post(`${API_BASE}/api/driver-agreement/save`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
    } catch (err) {
      console.error("Failed to save effective date", err);
    }
  };

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Driver Employee Agreement
        </span>
        <button className="btn btn-secondary" onClick={handleCancel}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4 px-4" style={{ lineHeight: 1.8 }}>
        <div className="row align-items-center mb-4">
          <div className="col-3">
            <img src={logo} alt="Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h3 className="m-0">DRIVER EMPLOYEE AGREEMENT</h3>
          </div>
          <CompanyAddress />
        </div>

        {/* Agreement Intro */}
        <p>
          This Driver Employee Agreement (“Agreement”) is entered into between
          <strong> New Way Transport Inc.</strong> (“Company”) and{" "}
          <strong>{username || "_________________________"}</strong> (“Driver”),
          effective{" "}
          <input
            type="date"
            className="form-control d-inline"
            style={{ width: "180px", marginLeft: "10px" }}
            value={effectiveDate}
            onChange={(e) => handleEffectiveDateChange(e.target.value)}
            disabled={!isAdmin && isSigned}
          />
          .
        </p>

        {/* PDF CONTENT */}
        <h5 className="my-4">1. POSITION & EMPLOYMENT STATUS</h5>
        <p>
          Driver is employed as a Commercial Motor Vehicle Driver (CDL)
          operating in interstate commerce subject to FMCSA regulations (49 CFR
          Parts 382, 383, 391, 392, 395).
        </p>
        <p>
          Employment is AT-WILL. Either the Company or Driver may terminate
          employment at any time, with or without cause or notice, subject to
          applicable law.
        </p>
        <p>Nothing in this Agreement creates a contract for a fixed term.</p>

        <hr className="my-4" />

        <h5>2. DUTIES & RESPONSIBILITIES</h5>
        <ul>
          <li>Operate Company equipment safely and lawfully</li>
          <li>Comply with FMCSA, DOT, state law, and customer requirements</li>
          <li>Follow Company policies, safety manuals, SOPs</li>
          <li>Complete pre-trip/post-trip inspections (DVIR)</li>
          <li>Accurately use ELD and comply with HOS</li>
          <li>Secure cargo properly and protect Company equipment</li>
          <li>
            Immediately report accidents, incidents, citations, delays, or
            injuries Failure to follow safety or compliance requirements may
            result in discipline or termination.
          </li>
        </ul>

        <hr className="my-4" />

        <h5>3. HOURS OF SERVICE & ELD</h5>
        <p>Driver must:</p>
        <ul>
          <li>Follow 49 CFR Part 395</li>
          <li>Use Company ELD correctly at all times</li>
          <li>Never falsify logs or operate while fatigued</li>
        </ul>
        <p>Log falsification is grounds for immediate termination.</p>
        <hr className="my-4" />

        <h5>4. DRUG & ALCOHOL COMPLIANCE</h5>
        <p>Driver is subject to:</p>
        <ul>
          <li>
            Pre-employment, random, post-accident, reasonable suspicion,
            return-to-duty, and follow-up testing
          </li>
          <li>FMCSA Drug & Alcohol Clearinghouse queries</li>
        </ul>
        <p>
          positive test, refusal, or violation will result in immediate removal
          from safety-sensitive functions and may result in termination.
        </p>
        <hr className="my-4" />

        <h5>5. DRIVER QUALIFICATION REQUIREMENTS</h5>
        <p>Employment is contingent upon maintaining:</p>
        <ul>
          <li>Valid CDL with required endorsements</li>
          <li>Valid DOT Medical Examiner’s Certificate</li>
          <li>Acceptable MVR and PSP</li>
          <li>Satisfactory safety performance history</li>
          <li>Eligibility to work in the U.S. (Form I-9)</li>
        </ul>
        <p>Loss or suspension of license must be reported immediately.</p>
        <hr className="my-4" />

        <h5>6. COMPANY PROPERTY & EQUIPMENT</h5>
        <p>All Company property remains Company-owned, including:</p>
        <ul>
          <li>Trucks, trailers, keys</li>
          <li>Fuel cards, ELD devices</li>
          <li>Seals, toll devices, documents</li>
        </ul>
        <p>Driver is financially responsible for:</p>
        <ul>
          <li>Negligent damage</li>
          <li>Lost equipment</li>
          <li>Unauthorized use</li>
        </ul>
        <p>
          Final pay may be withheld only as allowed by Illinois law to recover
          documented losses.
        </p>
        <hr className="my-4" />

        <h5>7. ACCIDENTS, INCIDENTS & CLAIMS</h5>
        <p>Driver must:</p>
        <ul>
          <li>
            Immediately notify dispatch/safety of any accident or incident
          </li>
          <li>Cooperate fully with investigations</li>
          <li>Never admit fault or authorize repairs without approval</li>
        </ul>
        <p>Failure to report promptly is grounds for discipline.</p>

        <hr className="my-4" />

        <h5>8. PAYROLL DEDUCTIONS (ILLINOIS COMPLIANT)</h5>
        <p>Driver authorizes lawful deductions including:</p>
        <ul>
          <li>Taxes and statutory deductions</li>
          <li>Court-ordered garnishments</li>
          <li>Voluntary deductions authorized in writing</li>
          <li>
            Equipment damage caused by negligence (with written acknowledgment)
          </li>
        </ul>
        <p>No illegal deductions will be made.</p>

        <hr className="my-4" />

        <h5>9. CONFIDENTIALITY & NON-SOLICITATION</h5>
        <p>Driver agrees not to:</p>
        <ul>
          <li>
            Disclose Company rates, customers, operations, or internal data
          </li>
          <li>
            Solicit Company customers or employees during employment and for 12
            months after separation
          </li>
        </ul>
        <p>This does not restrict lawful employment elsewhere.</p>

        <hr className="my-4" />

        <h5>10. SAFETY & ZERO-TOLERANCE POLICIES</h5>
        <p>
          <strong>Immediate termination may occur for:</strong>
        </p>
        <ul>
          <li>DUI/DWI</li>
          <li>Drug or alcohol violations</li>
          <li>Theft, fraud, or dishonesty</li>
          <li>Violence or threats</li>
          <li>Harassment or discrimination</li>
          <li>Safety misconduct</li>
        </ul>

        <hr className="my-4" />

        <h5>11. GOVERNING LAW</h5>
        <p>
          This Agreement is governed by the laws of the State of Illinois and
          applicable federal law.
        </p>

        <hr className="my-4" />

        <h5>12. ENTIRE AGREEMENT</h5>
        <p>
          This Agreement, together with Company policies and signed onboarding
          documents, constitutes the entire agreement.
        </p>
        <p>
          Modifications must be in writing and signed by Company management.
        </p>

        <hr className="my-4" />

        <h5>13. ACKNOWLEDGMENT & ACCEPTANCE</h5>
        <p>
          I acknowledge that I have read, understand, and agree to this
          Agreement.
        </p>

        <form>
          <div className="row mb-5 mt-4">
            <div className="col">
              <label className="form-label">Driver Name</label>
              <input
                type="text"
                className="form-control"
                value={username}
                readOnly
              />
            </div>

            <div className="col">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={
                  isSigned && signedDate
                    ? new Date(signedDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                readOnly
              />
            </div>
          </div>
        </form>

        <hr className="my-4" />

        {/* Attachment Upload */}
        <div className="mt-4">
          <label className="form-label">Attachment (PDF / JPG / PNG)</label>

          <input
            type="file"
            className="form-control"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            disabled={!isAdmin && isSigned}
          />

          <div className="d-flex justify-content-end mt-2">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUploadClick}
              disabled={!file || (!isAdmin && isSigned)}
            >
              Upload
            </button>
          </div>
        </div>

        <UploadedAttachmentList
          title="Uploaded Version"
          files={
            uploadedFileName
              ? [{ filename: uploadedFileName, original: originalFileName }]
              : []
          }
          getFileKey={(file) => file.filename}
          getFileName={(file) => file.original}
          getFileViewUrl={(file) =>
            `${API_BASE}/uploads/driver_agreements/${file.filename}`
          }
        />

        <hr className="my-5" />

        {/* Signature */}
        {userId && (
          <>
            <SignatureManager
              taskName="driver_employee_agreement"
              taskId={63}
              userId={userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="driver_employee_agreement"
              taskId={63}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DriverEmployeeAgreement;
